/**
 * Print-Only Business Envisioning Page for PDF Generation
 * Renders business envisioning data (Sections A-O) for PDF export
 *
 * URL: /print/business-envisioning/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';
import { formatPercentage, formatCurrency } from '../../utils/enhancedDashboardUtils';
import { BUSINESS_CATEGORY_KEY_MAP, BUSINESS_CATEGORY_DESCRIPTIONS } from '../../utils/categoryDescriptions';
import { SCORE_THRESHOLDS } from '../../utils/enhancedDashboardConstants';

// PDF-specific color functions that return hex codes instead of Tailwind classes
const getPdfScoreColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return '#8b6f47';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return '#16a34a'; // green-600
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return '#2563eb'; // blue-600
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return '#ca8a04'; // yellow-600
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return '#ea580c'; // orange-600
  return '#dc2626'; // red-600
};

const getPdfScoreBgColor = (score) => {
  if (score > SCORE_THRESHOLDS.NO_GAPS) return '#f5e6d3';
  if (score > SCORE_THRESHOLDS.MINOR_GAPS) return '#f0fdf4'; // green-50
  if (score > SCORE_THRESHOLDS.CONSIDERABLE_GAPS) return '#eff6ff'; // blue-50
  if (score > SCORE_THRESHOLDS.CRITICAL_GAPS) return '#fefce8'; // yellow-50
  if (score > SCORE_THRESHOLDS.VERY_CRITICAL_GAPS) return '#fff7ed'; // orange-50
  return '#fef2f2'; // red-50
};

const PrintBusinessEnvisioning = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [envisioningData, setEnvisioningData] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [wealthGap, setWealthGap] = useState(null);
  const [valueGap, setValueGap] = useState(null);
  const [profitGap, setProfitGap] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter (for PDF generation) or localStorage (for direct viewing)
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch all required data in parallel
        const [envisioningResponse, profileResponse, assessmentResponse, wealthGapResponse, valueGapResponse, profitGapResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business-envisioning`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/assessment/current`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/wealth-gap`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/value-gap`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/profit-gap`, { headers })
        ]);

        if (!envisioningResponse.ok) throw new Error('Failed to fetch envisioning data');
        if (!profileResponse.ok) throw new Error('Failed to fetch business profile');

        const envisioningJson = await envisioningResponse.json();
        const profileJson = await profileResponse.json();

        // Parse all JSON responses first
        let assessmentJson = null;
        let wealthJson = null;
        let valueJson = null;
        let profitJson = null;

        setEnvisioningData(envisioningJson);
        setBusinessData(profileJson.data.profile);

        // Parse assessment data - Note: backend returns data directly, not wrapped in {success, data}
        if (assessmentResponse.ok) {
          assessmentJson = await assessmentResponse.json();
          // Backend returns assessment data directly
          if (assessmentJson && assessmentJson.id) {
            setAssessmentData(assessmentJson);
          } else {
            console.warn('[PrintBusinessEnvisioning] Assessment data not valid:', assessmentJson);
          }
        } else {
          console.error('[PrintBusinessEnvisioning] Assessment response not ok:', assessmentResponse.status);
        }

        // Parse gap data - Note: backend returns {wealth_gap: {...}}, not {success, data: {...}}
        if (wealthGapResponse.ok) {
          wealthJson = await wealthGapResponse.json();
          if (wealthJson.wealth_gap) {
            setWealthGap(wealthJson.wealth_gap);
          }
        }

        if (valueGapResponse.ok) {
          valueJson = await valueGapResponse.json();
          if (valueJson.value_gap) {
            setValueGap(valueJson.value_gap);
          }
        }

        if (profitGapResponse.ok) {
          profitJson = await profitGapResponse.json();
          if (profitJson.profit_gap) {
            setProfitGap(profitJson.profit_gap);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('[PrintBusinessEnvisioning] Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && envisioningData) {
      document.body.setAttribute('data-pdf-ready', 'true');

      // Set document title
      const businessName = businessData?.business_name || 'Business';
      document.title = `${businessName} - Business Envisioning Report`;
    }
  }, [loading, envisioningData, businessData]);

  // Calculate gap scores from assessment (actual scores, not inverted)
  const calculateGapScores = () => {
    if (!assessmentData || !assessmentData.category_scores) {
      console.warn('[calculateGapScores] No assessment data or category scores');
      return { overallGapScore: 0, businessAssessmentGap: 0, familyEnterpriseGap: 0, attractivenessGap: 0, readinessGap: 0 };
    }

    const categoryScores = assessmentData.category_scores;

    // Business categories - matches web app
    const businessCategories = [
      categoryScores.financial_performance, categoryScores.revenue_quality, categoryScores.customer_concentration,
      categoryScores.management_team, categoryScores.competitive_position, categoryScores.growth_potential,
      categoryScores.intellectual_property, categoryScores.legal_compliance, categoryScores.owner_dependency,
      categoryScores.strategic_positioning // NOTE: strategic_positioning, not succession_planning
    ];

    // Family categories - matches web app
    const familyCategories = [
      categoryScores.boundary_management, categoryScores.family_council, categoryScores.equity_planning,
      categoryScores.wealth_transfer, categoryScores.family_governing, categoryScores.family_conflict,
      categoryScores.values_mission, categoryScores.succession_planning, // NOTE: succession_planning is in family
      categoryScores.employment_policies, categoryScores.education_development
    ];

    const businessValidScores = businessCategories.filter(score => score != null && score > 0);
    const familyValidScores = familyCategories.filter(score => score != null && score > 0);

    const businessAvg = businessValidScores.length > 0
      ? businessValidScores.reduce((sum, score) => sum + score, 0) / businessValidScores.length
      : 0;
    const familyAvg = familyValidScores.length > 0
      ? familyValidScores.reduce((sum, score) => sum + score, 0) / familyValidScores.length
      : 0;

    // Calculate overall as average of business and family (matches web app)
    const overallAvg = (businessAvg > 0 && familyAvg > 0)
      ? (businessAvg + familyAvg) / 2
      : (businessAvg > 0 ? businessAvg : familyAvg);


    // Return actual scores (NOT inverted) - matches web app behavior
    return {
      overallGapScore: overallAvg,
      businessAssessmentGap: businessAvg,
      familyEnterpriseGap: familyAvg,
      attractivenessGap: assessmentData.attractiveness_score || 0,
      readinessGap: assessmentData.readiness_score || 0
    };
  };

  // Get top strengths and risks
  const getTopStrengthsAndRisks = () => {
    if (!assessmentData || !assessmentData.category_scores) {
      console.warn('[getTopStrengthsAndRisks] No assessment data or category scores');
      return { autoStrengths: [], autoRisks: [] };
    }

    const categoryScores = assessmentData.category_scores;

    const scoredCategories = Object.entries(categoryScores)
      .filter(([key]) => {
        const hasMapping = !!BUSINESS_CATEGORY_KEY_MAP[key];
        if (!hasMapping) {
          console.warn('[getTopStrengthsAndRisks] No mapping found for key:', key);
        }
        return hasMapping;
      })
      .map(([key, score]) => {
        const categoryName = BUSINESS_CATEGORY_KEY_MAP[key];
        const categoryDesc = BUSINESS_CATEGORY_DESCRIPTIONS[categoryName];
        return {
          key,
          name: categoryName,
          score: score || 0,
          summary: categoryDesc?.summary || 'Assessment category measuring business strength.'
        };
      })
      .sort((a, b) => b.score - a.score);


    const autoStrengths = scoredCategories.slice(0, 3);
    const autoRisks = scoredCategories.slice(-3).reverse();


    return { autoStrengths, autoRisks };
  };

  // Calculate intangible capital scores from assessment responses (matches web app exactly)
  const getIntangibleScores = () => {
    if (!assessmentData || !assessmentData.responses || assessmentData.responses.length === 0) {
      return { Human: 0, Structural: 0, Customer: 0, Social: 0 };
    }

    // ID to Question ID mapping (matching web app BusinessEnvisioning.jsx)
    const idToQuestionId = {
      1: 'FIN-001', 2: 'FIN-002', 3: 'FIN-003', 4: 'FIN-004', 5: 'FIN-005',
      6: 'FIN-006', 7: 'FIN-007', 8: 'FIN-008', 9: 'FIN-009', 10: 'FIN-010',
      11: 'REV-001', 12: 'REV-002', 13: 'REV-003', 14: 'REV-004', 15: 'REV-005',
      16: 'REV-006', 17: 'REV-007', 18: 'REV-008', 19: 'REV-009', 20: 'CUST-001',
      21: 'CUST-002', 22: 'CUST-003', 23: 'CUST-004', 24: 'CUST-005', 25: 'CUST-006',
      26: 'CUST-007', 27: 'CUST-008', 28: 'MGT-001', 29: 'MGT-002', 30: 'MGT-003',
      31: 'MGT-004', 32: 'MGT-005', 33: 'MGT-006', 34: 'MGT-007', 35: 'MGT-008',
      36: 'MGT-009', 37: 'MGT-010', 38: 'COMP-001', 39: 'COMP-002', 40: 'COMP-003',
      41: 'COMP-004', 42: 'COMP-005', 43: 'COMP-006', 44: 'COMP-007', 45: 'COMP-008',
      46: 'GROW-001', 47: 'GROW-002', 48: 'GROW-003', 49: 'GROW-004', 50: 'GROW-005',
      51: 'GROW-006', 52: 'GROW-007', 53: 'GROW-008', 54: 'GROW-009', 55: 'IP-001',
      56: 'IP-002', 57: 'IP-003', 58: 'IP-004', 59: 'IP-005', 60: 'IP-006',
      61: 'IP-007', 62: 'IP-008', 63: 'LEGAL-001', 64: 'LEGAL-002', 65: 'LEGAL-003',
      66: 'LEGAL-004', 67: 'LEGAL-005', 68: 'LEGAL-006', 69: 'LEGAL-007', 70: 'LEGAL-008',
      71: 'LEGAL-009', 72: 'OWNER-001', 73: 'OWNER-002', 74: 'OWNER-003', 75: 'OWNER-004',
      76: 'OWNER-005', 77: 'OWNER-006', 78: 'OWNER-007', 79: 'OWNER-008', 80: 'OWNER-009',
      81: 'OWNER-010', 82: 'STRAT-001', 83: 'STRAT-002', 84: 'STRAT-003', 85: 'STRAT-004',
      86: 'STRAT-005', 87: 'STRAT-006', 88: 'STRAT-007', 89: 'STRAT-008'
    };

    const convertIdsToQuestionIds = (numericIds) => {
      return numericIds.map(id => idToQuestionId[id]);
    };

    // Intangible Asset Mapping (exactly matching web app BusinessEnvisioning.jsx)
    const intangibleAssetMapping = {
      'Human': [
        ...convertIdsToQuestionIds([28, 30, 75, 33, 74, 35, 31, 80, 62, 76, 32, 29, 67]),
        // Family Enterprise Questions
        'BOARD-004', 'BOARD-006', 'DEV-001', 'DEV-002', 'DEV-003', 'DEV-004',
        'DEV-005', 'DEV-006', 'DEV-007', 'DEV-008', 'EMP-002', 'EMP-003',
        'EMP-004', 'EMP-006', 'EMP-007', 'EMP-008', 'GOV-008', 'SUCC-002',
        'SUCC-003', 'SUCC-004', 'SUCC-006', 'SUCC-007', 'SUCC-008', 'VALUES-002',
        'VALUES-003', 'VALUES-005', 'VALUES-006', 'VALUES-008'
      ],
      'Structural': [
        ...convertIdsToQuestionIds([55, 66, 58, 81, 54, 79, 48, 37, 60, 47, 19, 57, 14, 16, 5, 56, 50, 12, 83, 10, 69, 42, 36, 70, 61, 53, 59, 34, 7, 64]),
        // Family Enterprise Questions
        'BOARD-001', 'BOARD-002', 'BOARD-003', 'BOARD-005', 'BOARD-007', 'BOARD-008',
        'EMP-001', 'EMP-005', 'EQUITY-001', 'EQUITY-002', 'EQUITY-003', 'EQUITY-004',
        'EQUITY-006', 'EQUITY-007', 'GOV-001', 'GOV-002', 'GOV-004', 'GOV-005',
        'GOV-006', 'GOV-007', 'RES-002', 'SUCC-001', 'SUCC-005', 'VALUES-001',
        'VALUES-004', 'VALUES-007', 'WTRANS-001', 'WTRANS-002', 'WTRANS-004',
        'WTRANS-006', 'WTRANS-007'
      ],
      'Customer': convertIdsToQuestionIds([26, 15, 24, 20, 23, 21, 27, 78, 86, 25, 22, 73]),
      'Social': [
        ...convertIdsToQuestionIds([88, 43, 40, 84, 82, 87, 38, 41, 85, 44, 89, 45, 39]),
        // Family Enterprise Questions
        'BOUND-001', 'BOUND-002', 'BOUND-003', 'BOUND-004', 'BOUND-005', 'BOUND-006',
        'BOUND-007', 'GOV-003', 'RES-001', 'RES-003', 'RES-004', 'RES-005',
        'RES-006', 'RES-007', 'RES-008'
      ]
    };

    const scores = {};
    const answeredQuestions = assessmentData.responses;

    Object.keys(intangibleAssetMapping).forEach(assetType => {
      const questionIds = intangibleAssetMapping[assetType];

      // Match questions by question_id
      const matchingResponses = answeredQuestions.filter(q => {
        return questionIds.includes(q.question_id);
      });

      // Calculate average score, excluding N/A (0) responses
      const validResponses = matchingResponses.filter(r => r.answer_value !== 0);
      if (validResponses.length > 0) {
        const totalScore = validResponses.reduce((sum, r) => sum + (r.score || 0), 0);
        scores[assetType] = totalScore / validResponses.length;
      } else {
        scores[assetType] = 0;
      }
    });

    return scores;
  };

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading business envisioning data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const data = envisioningData || {};
  const gapScores = calculateGapScores();
  const { autoStrengths, autoRisks } = getTopStrengthsAndRisks();
  const intangibleScores = getIntangibleScores();

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const parseJSON = (field) => {
    try {
      return typeof field === 'string' ? JSON.parse(field) : field;
    } catch {
      return field || [];
    }
  };

  // Helper to format percentage
  const formatPercentageValue = (value) => {
    if (!value && value !== 0) return 'Not specified';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const tenYearExitOptions = parseJSON(data.ten_year_exit_options) || [];
  const responsibilitiesKeep = parseJSON(data.responsibilities_keep) || [];
  const responsibilitiesStop = parseJSON(data.responsibilities_stop) || [];
  const fiveDsDeathItems = parseJSON(data.five_ds_death_items) || [];
  const fiveDsDisabilityItems = parseJSON(data.five_ds_disability_items) || [];
  const fiveDsDivorceItems = parseJSON(data.five_ds_divorce_items) || [];
  const fiveDsDistressItems = parseJSON(data.five_ds_distress_items) || [];
  const fiveDsDisagreementItems = parseJSON(data.five_ds_disagreement_items) || [];
  const exitPathPreferences = parseJSON(data.exit_path_preferences) || [];
  const constraints = parseJSON(data.constraints) || [];
  const quickWins = parseJSON(data.quick_wins) || [];
  const longerTermProjects = parseJSON(data.longer_term_projects) || [];
  const strategicPriorities = parseJSON(data.strategic_priorities) || [];

  // Get all advisory team members (standard + custom)
  const getAllAdvisors = () => {
    const advisorTypes = [
      { key: 'attorney', label: 'Business Attorney', hasKey: 'has_attorney' },
      { key: 'accountant', label: 'CPA / Accountant', hasKey: 'has_accountant' },
      { key: 'financial_advisor', label: 'Financial Advisor', hasKey: 'has_financial_advisor' },
      { key: 'exit_advisor', label: 'Exit / M&A Advisor', hasKey: 'has_exit_advisor' },
      { key: 'insurance_agent', label: 'Insurance Agent', hasKey: 'has_insurance_agent' },
      { key: 'banker', label: 'Banker / Lender', hasKey: 'has_banker' },
      { key: 'estate_planner', label: 'Estate Planner', hasKey: 'has_estate_planner' },
      { key: 'business_coach', label: 'Business Coach', hasKey: 'has_business_coach' }
    ];

    const advisors = [];

    // Add standard advisors
    advisorTypes.forEach(({ key, label, hasKey }) => {
      if (businessData?.[hasKey] && businessData?.[`${key}_name`]) {
        advisors.push({
          type: 'standard',
          role: label,
          name: businessData[`${key}_name`],
          email: businessData[`${key}_email`],
          phone: businessData[`${key}_phone`]
        });
      }
    });

    // Add custom advisors
    const customAdvisors = parseJSON(businessData?.custom_advisors) || [];
    if (Array.isArray(customAdvisors)) {
      customAdvisors.forEach(advisor => {
        if (advisor && (advisor.name || advisor.title || advisor.email || advisor.phone)) {
          advisors.push({
            type: 'custom',
            role: advisor.title || 'Custom Advisor',
            name: advisor.name,
            email: advisor.email,
            phone: advisor.phone
          });
        }
      });
    }

    return advisors;
  };

  const advisors = getAllAdvisors();

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <div className="cover-header">
          <h1>Business Envisioning Report</h1>
          <div className="report-type">3-10 Year Business Vision & Exit Strategy</div>
        </div>

        <div className="cover-business-info">
          <h2>{businessData?.business_name || 'Business Name Not Set'}</h2>
          {businessData?.industry && <p className="industry">{businessData.industry}</p>}
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Business Envisioning Report</p>
        </div>
      </div>

      {/* Section A: Current Business Position */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section A: Current Business Position</h1>

        {/* Gap Assessment Scores */}
        {assessmentData && assessmentData.answered_questions > 0 ? (
          <div className="content-block" style={{ marginBottom: '0.15in' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in' }}>Gap Assessment Scores</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.1in' }}>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(gapScores.overallGapScore) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(gapScores.overallGapScore), marginBottom: '0.05in' }}>
                  {formatPercentage(gapScores.overallGapScore)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Overall</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(gapScores.businessAssessmentGap) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(gapScores.businessAssessmentGap), marginBottom: '0.05in' }}>
                  {formatPercentage(gapScores.businessAssessmentGap)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Business</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(gapScores.familyEnterpriseGap) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(gapScores.familyEnterpriseGap), marginBottom: '0.05in' }}>
                  {formatPercentage(gapScores.familyEnterpriseGap)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Family</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(gapScores.attractivenessGap) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(gapScores.attractivenessGap), marginBottom: '0.05in' }}>
                  {formatPercentage(gapScores.attractivenessGap)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Attractive</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.1in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(gapScores.readinessGap) }}>
                <p style={{ fontSize: '17pt', fontWeight: 'bold', color: getPdfScoreColor(gapScores.readinessGap), marginBottom: '0.05in' }}>
                  {formatPercentage(gapScores.readinessGap)}
                </p>
                <p style={{ fontSize: '8pt', color: '#4b5563', fontWeight: '600' }}>Readiness</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="content-block" style={{ marginBottom: '0.15in', padding: '0.15in', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', textAlign: 'center' }}>
            <p style={{ fontSize: '8pt', color: '#6b7280' }}>No assessment data available</p>
          </div>
        )}

        {/* Top 3 Strengths */}
        {autoStrengths.length > 0 && (
          <div className="content-block" style={{ marginBottom: '0.15in' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in' }}>Top 3 Strengths</h3>
            {autoStrengths.map((strength, idx) => (
              <div key={idx} style={{ padding: '0.1in', marginBottom: '0.08in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(strength.score) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.05in' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: getPdfScoreColor(strength.score), color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8pt', fontWeight: 'bold', marginRight: '0.1in' }}>
                      {idx + 1}
                    </div>
                    <span style={{ fontSize: '8pt', fontWeight: '600' }}>{strength.name}</span>
                  </div>
                  <span style={{ fontSize: '10pt', fontWeight: 'bold', color: getPdfScoreColor(strength.score) }}>{formatPercentage(strength.score)}</span>
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', marginLeft: '0.3in' }}>{strength.summary}</p>
              </div>
            ))}
          </div>
        )}

        {/* Top 3 Risks/Gaps */}
        {autoRisks.length > 0 && (
          <div className="content-block" style={{ marginBottom: '0.15in' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in' }}>Top 3 Risks/Gaps</h3>
            {autoRisks.map((risk, idx) => (
              <div key={idx} style={{ padding: '0.1in', marginBottom: '0.08in', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: getPdfScoreBgColor(risk.score) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.05in' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: getPdfScoreColor(risk.score), color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8pt', fontWeight: 'bold', marginRight: '0.1in' }}>
                      {idx + 1}
                    </div>
                    <span style={{ fontSize: '8pt', fontWeight: '600' }}>{risk.name}</span>
                  </div>
                  <span style={{ fontSize: '10pt', fontWeight: 'bold', color: getPdfScoreColor(risk.score) }}>{formatPercentage(risk.score)}</span>
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', marginLeft: '0.3in' }}>{risk.summary}</p>
              </div>
            ))}
          </div>
        )}

        {/* Business Model Summary */}
        {data.business_model_summary && (
          <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Business Model Summary</h3>
                <p className="text-content">{data.business_model_summary}</p>
              </div>
        )}
      </div>

      {/* Section B: Three Gaps Analysis */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section B: Three Gaps Analysis</h1>

        {/* Value Gap */}
        {valueGap && (
          <div className="content-block" style={{ marginBottom: '0.2in', padding: '0.15in', backgroundColor: '#ecfdf5', border: '2px solid #a7f3d0', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in' }}>Value Gap</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.1in' }}>
              <div style={{ padding: '0.1in', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.05in' }}>Current State</p>
                <p style={{ fontSize: '8pt', marginBottom: '0.03in' }}>Current Est. Business Exit Value: <strong>{formatCurrency(valueGap.current_exit_value)}</strong></p>
                <p style={{ fontSize: '8pt' }}>Current Overall Business Gap Score: <strong style={{ color: '#f59e0b' }}>{valueGap.current_attractiveness?.toFixed(1)}%</strong></p>
              </div>
              <div style={{ padding: '0.1in', backgroundColor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '6px' }}>
                <p style={{ fontSize: '8pt', fontWeight: '600', color: '#1e40af', marginBottom: '0.05in' }}>Green Zone ({valueGap.green_zone_attractiveness?.toFixed(1)}%)</p>
                <p style={{ fontSize: '8pt', color: '#1e40af' }}>Valuation Impact: <strong style={{ fontSize: '9pt' }}>{formatCurrency(valueGap.value_impact_green_zone)}</strong></p>
                <p style={{ fontSize: '8pt', color: '#1e40af' }}>Additional value from reaching green zone</p>
              </div>
              <div style={{ padding: '0.1in', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px' }}>
                <p style={{ fontSize: '8pt', fontWeight: '600', color: '#166534', marginBottom: '0.05in' }}>Best in Class ({valueGap.best_in_class_attractiveness?.toFixed(1)}%)</p>
                <p style={{ fontSize: '8pt', color: '#166534' }}>Valuation Impact: <strong style={{ fontSize: '9pt' }}>{formatCurrency(valueGap.value_impact_best_in_class)}</strong></p>
                <p style={{ fontSize: '8pt', color: '#166534' }}>Additional value from best in class</p>
              </div>
            </div>
          </div>
        )}

        {/* Profit Gap */}
        {profitGap && (
          <div className="content-block" style={{ marginBottom: '0.2in', padding: '0.15in', backgroundColor: '#faf5ff', border: '2px solid #d8b4fe', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in' }}>Profit Gap</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.1in' }}>
              <div style={{ padding: '0.1in', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.05in' }}>Current State</p>
                <p style={{ fontSize: '8pt', marginBottom: '0.03in' }}>Current Est. Business Exit Value: <strong>{formatCurrency(profitGap.current_valuation)}</strong></p>
                <p style={{ fontSize: '8pt' }}>Current EBITDA Margin: <strong style={{ color: '#f59e0b' }}>{profitGap.current_ebitda_margin?.toFixed(1)}%</strong></p>
              </div>
              <div style={{ padding: '0.1in', backgroundColor: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '6px' }}>
                <p style={{ fontSize: '8pt', fontWeight: '600', color: '#1e40af', marginBottom: '0.05in' }}>Median Margin ({profitGap.industry_median_ebitda_margin?.toFixed(1)}%)</p>
                <p style={{ fontSize: '8pt', color: '#1e40af' }}>Valuation Impact: <strong style={{ fontSize: '9pt' }}>{formatCurrency(profitGap.valuation_impact_median)}</strong></p>
                <p style={{ fontSize: '8pt', color: '#1e40af' }}>Additional value from median margin</p>
              </div>
              <div style={{ padding: '0.1in', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px' }}>
                <p style={{ fontSize: '8pt', fontWeight: '600', color: '#166534', marginBottom: '0.05in' }}>Best in Class ({profitGap.industry_high_ebitda_margin?.toFixed(1)}%)</p>
                <p style={{ fontSize: '8pt', color: '#166534' }}>Valuation Impact: <strong style={{ fontSize: '9pt' }}>{formatCurrency(profitGap.valuation_impact_best)}</strong></p>
                <p style={{ fontSize: '8pt', color: '#166534' }}>Additional value from best in class</p>
              </div>
            </div>
          </div>
        )}

        {/* Wealth Gap */}
        {wealthGap && (
          <div className="content-block" style={{ padding: '0.15in', backgroundColor: '#fff7ed', border: '2px solid #fed7aa', borderRadius: '8px' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in' }}>Wealth Gap</h3>

            {/* Calculate metrics */}
            {(() => {
              const currentYear = new Date().getFullYear();
              const yearsUntilExit = Math.max(0, (wealthGap.exit_year || currentYear + 2) - currentYear);
              const ageAtExit = wealthGap.current_age + yearsUntilExit;

              // Calculate outside income growth
              const outsideIncomeGrowthRate = (wealthGap.outside_income_annual_increase || 0) / 100;
              const yearsUntilRetirement = Math.max(0, wealthGap.retirement_age - wealthGap.current_age);
              const futureOutsideIncomeMonthly = (wealthGap.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);

              // Calculate monthly need at exit with inflation
              const inflationMultiplier = Math.pow(1 + (wealthGap.annual_inflation_rate || 2) / 100, yearsUntilExit);
              const monthlyNeedAtExit = wealthGap.monthly_cash_need * inflationMultiplier * (1 + (wealthGap.lifestyle_change_percentage || 0) / 100);

              // Current and future net monthly needs
              const currentNetMonthlyNeed = Math.max(0, wealthGap.monthly_cash_need - (wealthGap.outside_income_monthly || 0));
              const netMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);

              // Get required sale price data
              const requiredData = wealthGap.required_sale_price_data || {};
              const hasRequiredData = wealthGap.wealth_goal_method === 'monthly_needs' && requiredData.total_capital_needed !== undefined;

              return (
                <>
                  {wealthGap.exit_year && (
                    <div style={{ marginBottom: '0.1in', padding: '0.08in', backgroundColor: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: '6px', textAlign: 'center' }}>
                      <p style={{ fontSize: '8pt' }}>
                        Exit Year: <strong style={{ color: '#7c3aed' }}>{wealthGap.exit_year}</strong>
                        {wealthGap.current_age && <> @ Age <strong style={{ color: '#7c3aed' }}>{ageAtExit}</strong></>}
                      </p>
                    </div>
                  )}

                  <div style={{ backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.1in' }}>
                    <table style={{ width: '100%', fontSize: '8pt', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                          <th style={{ textAlign: 'left', padding: '0.05in', fontWeight: '600' }}></th>
                          <th style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>Current</th>
                          <th style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>At Exit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wealthGap.wealth_goal_method === 'monthly_needs' && (
                          <>
                            <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.05in', fontWeight: '600' }}>Net Monthly Needs</td>
                              <td style={{ textAlign: 'center', padding: '0.05in', color: '#2563eb', fontWeight: '600' }}>
                                {formatCurrency(currentNetMonthlyNeed)}
                              </td>
                              <td style={{ textAlign: 'center', padding: '0.05in', color: '#2563eb', fontWeight: '600' }}>
                                {formatCurrency(netMonthlyNeed)}
                              </td>
                            </tr>
                            {hasRequiredData && (
                              <tr>
                                <td style={{ padding: '0.05in', fontWeight: '600' }}>Capital Required</td>
                                <td style={{ textAlign: 'center', padding: '0.05in', color: '#059669', fontWeight: '600' }}>
                                  -
                                </td>
                                <td style={{ textAlign: 'center', padding: '0.05in', color: '#059669', fontWeight: '600' }}>
                                  {formatCurrency(requiredData.total_capital_needed)}
                                </td>
                              </tr>
                            )}
                          </>
                        )}
                        {wealthGap.wealth_goal_method === 'lump_sum' && (
                          <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.05in', fontWeight: '600' }}>Target Net Worth</td>
                            <td style={{ textAlign: 'center', padding: '0.05in', color: '#059669', fontWeight: '600' }}>-</td>
                            <td style={{ textAlign: 'center', padding: '0.05in', color: '#059669', fontWeight: '600' }}>
                              {formatCurrency(wealthGap.calculated_wealth_goal)}
                            </td>
                          </tr>
                        )}
                        <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.05in', fontWeight: '600' }}>Personal Net Worth</td>
                          <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>
                            {formatCurrency(wealthGap.current_net_worth)}
                          </td>
                          <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600' }}>
                            {formatCurrency(hasRequiredData ? requiredData.future_net_worth : wealthGap.current_net_worth)}
                          </td>
                        </tr>
                        {(wealthGap.future_business_value || wealthGap.desired_sale_price) && (
                          <tr style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#eff6ff' }}>
                            <td style={{ padding: '0.05in', fontWeight: '600' }}>Est. Business Exit Value</td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600', color: '#2563eb' }}>
                              {formatCurrency(wealthGap.desired_sale_price || 0)}
                            </td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '600', color: '#2563eb' }}>
                              {formatCurrency(wealthGap.future_business_value || wealthGap.desired_sale_price || 0)}
                            </td>
                          </tr>
                        )}
                        {hasRequiredData && (
                          <tr style={{ borderTop: '2px solid #d1d5db', backgroundColor: requiredData.wealth_surplus > 0 ? '#f0fdf4' : '#fef2f2' }}>
                            <td style={{ padding: '0.05in', fontWeight: '700', fontSize: '8pt' }}>
                              {requiredData.wealth_surplus > 0 ? 'Wealth Surplus' : 'Gap from Business'}
                            </td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '700', fontSize: '8pt', color: requiredData.wealth_surplus > 0 ? '#059669' : '#dc2626' }}>
                              -
                            </td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '700', fontSize: '8pt', color: requiredData.wealth_surplus > 0 ? '#059669' : '#dc2626' }}>
                              {formatCurrency(requiredData.wealth_surplus > 0 ? requiredData.wealth_surplus : requiredData.net_gap_from_business)}
                            </td>
                          </tr>
                        )}
                        {hasRequiredData && requiredData.net_gap_from_business > 0 && (
                          <tr style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#fff7ed' }}>
                            <td style={{ padding: '0.05in', fontWeight: '700', fontSize: '8pt' }}>Required Sale Price</td>
                            <td style={{ textAlign: 'center', padding: '0.05in' }}>-</td>
                            <td style={{ textAlign: 'center', padding: '0.05in', fontWeight: '700', fontSize: '8pt', color: '#ea580c' }}>
                              {formatCurrency(requiredData.required_sale_price)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Section C: Four Capitals Assessment */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section C: Four Capitals Assessment</h1>

        {/* Human Capital */}
        <div className="content-block" style={{ marginBottom: '0.15in', borderLeft: '4px solid #6366f1', paddingLeft: '0.15in' }}>
          <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.1in' }}>Human Capital</h3>
          {intangibleScores.Human > 0 ? (
            <div style={{ padding: '0.1in', backgroundColor: getPdfScoreBgColor(intangibleScores.Human), border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '0.1in' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.15in' }}>
                <div style={{ fontSize: '19pt', fontWeight: 'bold', color: getPdfScoreColor(intangibleScores.Human) }}>
                  {formatPercentage(intangibleScores.Human)}
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', flex: 1 }}>
                  The collective skills, knowledge, and capabilities of your workforce. This includes employee expertise, training programs, leadership quality, and organizational culture that drives performance.
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '0.1in', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'center', marginBottom: '0.1in' }}>
              <p style={{ fontSize: '8pt', color: '#6b7280' }}>Complete assessment to see rating</p>
            </div>
          )}
          {data.human_capital_vision && (
            <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
              <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '600' }}>3-5 Year Vision:</h3>
              <p style={{ fontSize: '8pt', color: '#4b5563' }}>{data.human_capital_vision}</p>
            </div>
          )}
        </div>

        {/* Structural Capital */}
        <div className="content-block" style={{ marginBottom: '0.15in', borderLeft: '4px solid #a855f7', paddingLeft: '0.15in' }}>
          <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.1in' }}>Structural Capital</h3>
          {intangibleScores.Structural > 0 ? (
            <div style={{ padding: '0.1in', backgroundColor: getPdfScoreBgColor(intangibleScores.Structural), border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '0.1in' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.15in' }}>
                <div style={{ fontSize: '19pt', fontWeight: 'bold', color: getPdfScoreColor(intangibleScores.Structural) }}>
                  {formatPercentage(intangibleScores.Structural)}
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', flex: 1 }}>
                  The systems, processes, databases, and intellectual property that remain with the company. This includes proprietary software, documented procedures, patents, and organizational infrastructure.
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '0.1in', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'center', marginBottom: '0.1in' }}>
              <p style={{ fontSize: '8pt', color: '#6b7280' }}>Complete assessment to see rating</p>
            </div>
          )}
          {data.structural_capital_vision && (
            <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
              <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '600' }}>3-5 Year Vision:</h3>
              <p style={{ fontSize: '8pt', color: '#4b5563' }}>{data.structural_capital_vision}</p>
            </div>
          )}
        </div>

        {/* Customer Capital */}
        <div className="content-block" style={{ marginBottom: '0.15in', borderLeft: '4px solid #3b82f6', paddingLeft: '0.15in' }}>
          <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.1in' }}>Customer Capital</h3>
          {intangibleScores.Customer > 0 ? (
            <div style={{ padding: '0.1in', backgroundColor: getPdfScoreBgColor(intangibleScores.Customer), border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '0.1in' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.15in' }}>
                <div style={{ fontSize: '19pt', fontWeight: 'bold', color: getPdfScoreColor(intangibleScores.Customer) }}>
                  {formatPercentage(intangibleScores.Customer)}
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', flex: 1 }}>
                  The value of your customer relationships, brand reputation, and market position. This includes customer loyalty, retention rates, brand equity, and the strength of customer relationships.
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '0.1in', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'center', marginBottom: '0.1in' }}>
              <p style={{ fontSize: '8pt', color: '#6b7280' }}>Complete assessment to see rating</p>
            </div>
          )}
          {data.customer_capital_vision && (
            <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
              <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '600' }}>3-5 Year Vision:</h3>
              <p style={{ fontSize: '8pt', color: '#4b5563' }}>{data.customer_capital_vision}</p>
            </div>
          )}
        </div>

        {/* Social Capital */}
        <div className="content-block" style={{ borderLeft: '4px solid #14b8a6', paddingLeft: '0.15in' }}>
          <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.1in' }}>Social Capital</h3>
          {intangibleScores.Social > 0 ? (
            <div style={{ padding: '0.1in', backgroundColor: getPdfScoreBgColor(intangibleScores.Social), border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '0.1in' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.15in' }}>
                <div style={{ fontSize: '19pt', fontWeight: 'bold', color: getPdfScoreColor(intangibleScores.Social) }}>
                  {formatPercentage(intangibleScores.Social)}
                </div>
                <p style={{ fontSize: '8pt', color: '#4b5563', flex: 1 }}>
                  The networks, partnerships, and relationships that create value. This includes strategic partnerships, industry connections, supplier relationships, and community engagement.
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding: '0.1in', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'center', marginBottom: '0.1in' }}>
              <p style={{ fontSize: '8pt', color: '#6b7280' }}>Complete assessment to see rating</p>
            </div>
          )}
          {data.social_capital_vision && (
            <div className="content-block" style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
              <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '600' }}>3-5 Year Vision:</h3>
              <p style={{ fontSize: '8pt', color: '#4b5563' }}>{data.social_capital_vision}</p>
            </div>
          )}
        </div>
      </div>

      {/* Continue with remaining sections D-K (existing code) */}
      {/* Section D: Purpose, Vision & Core Values */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section D: Purpose, Vision & Core Values</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Define why your business exists and where it's going
        </p>

        {/* Two-column grid layout with borders */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>

          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {data.vision_statement && (
              <div style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Vision Statement</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data.vision_statement}</p>
              </div>
            )}

            {data.mission_statement && (
              <div style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Mission Statement</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data.mission_statement}</p>
              </div>
            )}

            {data.reason_to_believe && (
              <div style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Reason to Believe</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data.reason_to_believe}</p>
              </div>
            )}

            {data.proof_points && (
              <div style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Proof Points</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data.proof_points}</p>
              </div>
            )}

            {data.tone_manner && (
              <div style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Tone & Manner</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data.tone_manner}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {data.kingdom_impact_goals && (
              <div style={{ padding: '0.1in', border: '2px solid #dbeafe', borderRadius: '6px', backgroundColor: '#f0f9ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>Kingdom Impact Goals</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data.kingdom_impact_goals}</p>
              </div>
            )}

            {/* Core Values */}
            {[1,2,3,4,5].map((num) => {
              const valueKey = `core_value_${num}`;
              const appliedKey = `core_value_${num}_applied`;
              if (data[valueKey]) {
                return (
                  <div key={num} style={{ padding: '0.1in', border: '2px solid #e0e7ff', borderRadius: '6px', backgroundColor: '#eef2ff', pageBreakInside: 'avoid' }}>
                    <h3 style={{ fontSize: '8pt', color: '#4338ca', marginBottom: '0.05in', fontWeight: '700' }}>Core Value {num}: {data[valueKey]}</h3>
                    {data[appliedKey] && <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data[appliedKey]}</p>}
                  </div>
                );
              }
              return null;
            })}

            {data.business_owner_type && (
              <div style={{ padding: '0.1in', border: '2px solid #e0e7ff', borderRadius: '6px', backgroundColor: '#eef2ff', pageBreakInside: 'avoid' }}>
                <h3 style={{ fontSize: '8pt', color: '#4338ca', marginBottom: '0.05in', fontWeight: '700' }}>Business Owner Type</h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', fontWeight: '600' }}>
                  {data.business_owner_type === 'lifestyle' && 'Lifestyle Business Owner'}
                  {data.business_owner_type === 'value_building' && 'Value Building Business Owner'}
                </p>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginTop: '0.05in' }}>
                  {data.business_owner_type === 'lifestyle' && 'Prioritize cash flow, flexibility, and personal lifestyle. The business supports your desired way of life.'}
                  {data.business_owner_type === 'value_building' && 'Prioritize growth and increasing enterprise value. Building toward an eventual exit or sale.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Core Values (6-10) if they exist - shown below in two columns */}
        {[6,7,8,9,10].some(num => data[`core_value_${num}`]) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', marginTop: '0.15in', pageBreakInside: 'avoid' }}>
            {[6,7,8,9,10].map((num) => {
              const valueKey = `core_value_${num}`;
              const appliedKey = `core_value_${num}_applied`;
              if (data[valueKey]) {
                return (
                  <div key={num} style={{ padding: '0.1in', border: '2px solid #e0e7ff', borderRadius: '6px', backgroundColor: '#eef2ff', pageBreakInside: 'avoid' }}>
                    <h3 style={{ fontSize: '8pt', color: '#4338ca', marginBottom: '0.05in', fontWeight: '700' }}>Core Value {num}: {data[valueKey]}</h3>
                    {data[appliedKey] && <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>{data[appliedKey]}</p>}
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>

      {/* Section E: 10-Year Destination */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section E: 10-Year Destination</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Paint a picture of where you're headed long-term
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {data.purpose_statement && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Purpose Statement
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.purpose_statement}
                </p>
              </div>
            )}

            {tenYearExitOptions.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  10-Year Exit Options
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {tenYearExitOptions.map((option, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{option}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            <div style={{
              padding: '0.1in',
              border: '2px solid #dbeafe',
              borderRadius: '6px',
              backgroundColor: '#f0f9ff',
              pageBreakInside: 'avoid'
            }}>
              <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                10-Year Vision
              </h3>
              <div style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                {data.ten_year_size && (
                  <div style={{ marginBottom: '0.05in' }}>
                    <span style={{ fontWeight: '600' }}>Size/Scale:</span> {data.ten_year_size}
                  </div>
                )}
                {data.ten_year_profitability && (
                  <div style={{ marginBottom: '0.05in' }}>
                    <span style={{ fontWeight: '600' }}>Profitability:</span> {data.ten_year_profitability}
                  </div>
                )}
                {data.ten_year_business_value && (
                  <div style={{ marginBottom: '0.05in' }}>
                    <span style={{ fontWeight: '600' }}>Business Value:</span> {data.ten_year_business_value}
                  </div>
                )}
                {data.ten_year_owner_role && (
                  <div>
                    <span style={{ fontWeight: '600' }}>Owner Role:</span> {data.ten_year_owner_role}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section F: 3-5 Year Business Picture */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section F: 3-5 Year Business Picture</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Define your near-term business targets
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {data.target_customers && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Target Customers
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.target_customers}
                </p>
              </div>
            )}

            {data.market_reputation && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Market Reputation
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.market_reputation}
                </p>
              </div>
            )}

            {data.competitive_position && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Competitive Position
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.competitive_position}
                </p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            <div style={{
              padding: '0.1in',
              border: '2px solid #dbeafe',
              borderRadius: '6px',
              backgroundColor: '#f0f9ff',
              pageBreakInside: 'avoid'
            }}>
              <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                3-Year Financial Targets
              </h3>
              <div style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                {data.three_year_revenue && (
                  <div style={{ marginBottom: '0.05in' }}>
                    <span style={{ fontWeight: '600' }}>Revenue Target:</span> {formatCurrency(data.three_year_revenue)}
                  </div>
                )}
                {data.three_year_ebitda_margin && (
                  <div style={{ marginBottom: '0.05in' }}>
                    <span style={{ fontWeight: '600' }}>EBITDA Margin:</span> {formatPercentageValue(data.three_year_ebitda_margin)}
                  </div>
                )}
                {data.three_year_recurring_revenue_pct && (
                  <div style={{ marginBottom: '0.05in' }}>
                    <span style={{ fontWeight: '600' }}>Recurring Revenue:</span> {formatPercentageValue(data.three_year_recurring_revenue_pct)}
                  </div>
                )}
                {(data.three_year_value_min || data.three_year_value_max) && (
                  <div>
                    <span style={{ fontWeight: '600' }}>Value Estimate Range:</span> {formatCurrency(data.three_year_value_min)} - {formatCurrency(data.three_year_value_max)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SWOT Analysis - Full Width Below */}
        <div style={{
          marginTop: '0.15in',
          padding: '0.1in',
          border: '2px solid #dbeafe',
          borderRadius: '6px',
          backgroundColor: '#f0f9ff',
          pageBreakInside: 'avoid'
        }}>
          <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
            SWOT Analysis
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in', fontSize: '8pt', color: '#374151' }}>
            {data.swot_strengths && (
              <div>
                <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.03in' }}>Strengths:</span>
                <p style={{ lineHeight: '1.4', margin: 0 }}>{data.swot_strengths}</p>
              </div>
            )}
            {data.swot_weaknesses && (
              <div>
                <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.03in' }}>Weaknesses:</span>
                <p style={{ lineHeight: '1.4', margin: 0 }}>{data.swot_weaknesses}</p>
              </div>
            )}
            {data.swot_opportunities && (
              <div>
                <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.03in' }}>Opportunities:</span>
                <p style={{ lineHeight: '1.4', margin: 0 }}>{data.swot_opportunities}</p>
              </div>
            )}
            {data.swot_threats && (
              <div>
                <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.03in' }}>Threats:</span>
                <p style={{ lineHeight: '1.4', margin: 0 }}>{data.swot_threats}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section G: Owner Role & Dependency */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section G: Owner Role & Dependency</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Plan how to make yourself optional
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {responsibilitiesKeep.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Responsibilities to Keep
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {responsibilitiesKeep.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.responsibilities_delegate && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Responsibilities to Delegate
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.responsibilities_delegate}
                </p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {responsibilitiesStop.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Responsibilities to Stop
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {responsibilitiesStop.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.sabbatical_readiness && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Sabbatical Readiness
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.sabbatical_readiness}
                </p>
              </div>
            )}
          </div>
        </div>

        {data.key_person_risk && (
          <div style={{
            marginTop: '0.15in',
            padding: '0.1in',
            border: '2px solid #dbeafe',
            borderRadius: '6px',
            backgroundColor: '#f0f9ff',
            pageBreakInside: 'avoid'
          }}>
            <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
              Key Person Risk
            </h3>
            <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
              {data.key_person_risk}
            </p>
          </div>
        )}
      </div>

      {/* Section H: Five Ds Preparedness */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section H: Five Ds Preparedness</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Death, Disability, Divorce, Distress, Disagreement
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {fiveDsDeathItems.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #e0e7ff',
                borderRadius: '6px',
                backgroundColor: '#eef2ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#3730a3', marginBottom: '0.05in', fontWeight: '700' }}>
                  Death Preparedness
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {fiveDsDeathItems.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
                {data.five_ds_death && (
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginTop: '0.08in' }}>
                    {data.five_ds_death}
                  </p>
                )}
              </div>
            )}

            {fiveDsDivorceItems.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #e0e7ff',
                borderRadius: '6px',
                backgroundColor: '#eef2ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#3730a3', marginBottom: '0.05in', fontWeight: '700' }}>
                  Divorce Preparedness
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {fiveDsDivorceItems.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
                {data.five_ds_divorce && (
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginTop: '0.08in' }}>
                    {data.five_ds_divorce}
                  </p>
                )}
              </div>
            )}

            {fiveDsDisagreementItems.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #e0e7ff',
                borderRadius: '6px',
                backgroundColor: '#eef2ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#3730a3', marginBottom: '0.05in', fontWeight: '700' }}>
                  Disagreement Preparedness
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {fiveDsDisagreementItems.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
                {data.five_ds_disagreement && (
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginTop: '0.08in' }}>
                    {data.five_ds_disagreement}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {fiveDsDisabilityItems.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #e0e7ff',
                borderRadius: '6px',
                backgroundColor: '#eef2ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#3730a3', marginBottom: '0.05in', fontWeight: '700' }}>
                  Disability Preparedness
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {fiveDsDisabilityItems.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
                {data.five_ds_disability && (
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginTop: '0.08in' }}>
                    {data.five_ds_disability}
                  </p>
                )}
              </div>
            )}

            {fiveDsDistressItems.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #e0e7ff',
                borderRadius: '6px',
                backgroundColor: '#eef2ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#3730a3', marginBottom: '0.05in', fontWeight: '700' }}>
                  Distress Preparedness
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {fiveDsDistressItems.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
                {data.five_ds_distress && (
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginTop: '0.08in' }}>
                    {data.five_ds_distress}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section I: Ideal Exit Scenario */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section I: Ideal Exit Scenario</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Define your preferred exit path and non-negotiables
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {data.exit_timing_window && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Exit Timing Window
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.exit_timing_window}
                </p>
              </div>
            )}

            {data.exit_red_lines && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Exit Red Lines (Deal Breakers)
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.exit_red_lines}
                </p>
              </div>
            )}

            {data.exit_transition_role && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Post-Exit Transition Role
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.exit_transition_role}
                </p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {exitPathPreferences.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Exit Path Preferences
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {exitPathPreferences.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.exit_deal_structure && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Preferred Deal Structure
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.exit_deal_structure}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section J: Constraints, Risks & Assumptions */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section J: Constraints, Risks & Assumptions</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Identify what could accelerate or delay your plan
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {constraints.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Key Constraints
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {constraints.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.acceleration_factors && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Acceleration Factors
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.acceleration_factors}
                </p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {data.key_assumptions && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Key Assumptions
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.key_assumptions}
                </p>
              </div>
            )}

            {data.delay_factors && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Delay Factors
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.delay_factors}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section K: 90-Day Strategic Focus */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section K: 90-Day Strategic Focus</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          What are you doing in the next 90 days to accelerate value?
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {quickWins.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Quick Wins
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {quickWins.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {data.value_acceleration_priorities && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Value Acceleration Priorities
                </h3>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4' }}>
                  {data.value_acceleration_priorities}
                </p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1in' }}>
            {longerTermProjects.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Longer-Term Projects
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {longerTermProjects.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {strategicPriorities.length > 0 && (
              <div style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  Strategic Priorities
                </h3>
                <ul style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', margin: '0', paddingLeft: '0.15in' }}>
                  {strategicPriorities.map((item, idx) => (
                    <li key={idx} style={{ marginBottom: '0.03in' }}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section L: Advisory Team */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        <h1 className="section-title">Section L: Advisory Team</h1>
        <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.15in', fontStyle: 'italic' }}>
          Professional advisors supporting your business
        </p>

        {advisors.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.15in', pageBreakInside: 'avoid' }}>
            {advisors.map((advisor, index) => (
              <div key={index} style={{
                padding: '0.1in',
                border: '2px solid #dbeafe',
                borderRadius: '6px',
                backgroundColor: '#f0f9ff',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ fontSize: '8pt', color: '#1e3a8a', marginBottom: '0.05in', fontWeight: '700' }}>
                  {advisor.role}
                </h3>
                {advisor.name && (
                  <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.4', marginBottom: '0.03in', fontWeight: '600' }}>
                    {advisor.name}
                  </p>
                )}
                {advisor.email && (
                  <p style={{ fontSize: '8pt', color: '#2563eb', lineHeight: '1.4', marginBottom: '0.02in' }}>
                    Email: {advisor.email}
                  </p>
                )}
                {advisor.phone && (
                  <p style={{ fontSize: '8pt', color: '#2563eb', lineHeight: '1.4' }}>
                    Phone: {advisor.phone}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            padding: '0.2in',
            border: '2px solid #e5e7eb',
            borderRadius: '6px',
            backgroundColor: '#f9fafb',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '8pt', color: '#6b7280', lineHeight: '1.4' }}>
              No advisory team members have been added yet
            </p>
          </div>
        )}

        {/* Advisory Team Notes */}
        {businessData?.advisory_notes && (
          <div style={{ marginTop: '0.15in', pageBreakInside: 'avoid' }}>
            <h3 style={{ fontSize: '9pt', color: '#1e3a8a', marginBottom: '0.08in', fontWeight: '700' }}>
              Advisory Team Notes
            </h3>
            <div style={{
              padding: '0.12in',
              border: '2px solid #dbeafe',
              borderRadius: '6px',
              backgroundColor: '#f0f9ff'
            }}>
              <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {businessData.advisory_notes}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintBusinessEnvisioning;
